<template>
  <v-app id="login">
    <v-content>
      <div v-if="!isRanOnLocal && !isLocationEnabled" class="location-disabled-msg">
        <b>IMPORTANT! </b>{{ locationDisplayMsg }}
      </div>
      <div id="overlay" v-show="!isRanOnLocal && !isLocationEnabled" />
      <div class="enerman">
        <v-layout row justify-center>
          <v-flex xs6 md5>
            <v-layout class="login-box">
              <v-flex md5 class="bg-img align-self-center">
                <v-tooltip>
                  <template slot="activator">
                    <a target="_blank">
                      <img
                        class="img"
                        src="./../assets/logo/enerman-etisol_new_dark.svg"
                        alt="enerman logo"
                        width="150px"
                        height="150px"
                      />
                    </a>
                  </template>
                </v-tooltip>
              </v-flex>
              <v-flex md7>
                <div class="login-inner-form">
                  <div class="details">
                    <h3 class="login-heading">
                      {{ $t(computedPageName) }} {{ $t("Password") }}
                    </h3>
                    <v-text-field
                      :label="$t('New Password')"
                      :type="showPassword ? 'text' : 'password'"
                      :rules="[
                        rules.required,
                        rules.min,
                        rules.max,
                        computedPasswordStrength.message,
                      ]"
                      v-model="password"
                      light
                      :placeholder="$t('New Password')"
                    >
                    </v-text-field>
                    <v-text-field
                      :label="$t('Confirm Password')"
                      :type="showPassword ? 'text' : 'password'"
                      :rules="[
                        rules.required,
                        rules.min,
                        rules.max,
                        computedPasswordStrength.message,
                      ]"
                      v-model="confirmPassword"
                      light
                      :placeholder="$t('Confirm Password')"
                    >
                    </v-text-field>
                    <v-progress-linear
                      background-opacity="0"
                      :color="computedPasswordStrength.color"
                      :value="progressValue"
                      light
                    ></v-progress-linear>
                    <v-checkbox
                      v-model="showPassword"
                      :label="$t('Show password')"
                      light
                    ></v-checkbox>
                    <v-btn
                      block
                      large
                      light
                      class="btn-md btn-theme"
                      :disabled="
                        (!isLocationEnabled && !isRanOnLocal) ||
                        computedPasswordStrength.score < 4
                      "
                      @click.prevent="validatePasswordHandler"
                      >{{ $t("Submit") }}
                    </v-btn>
                    <div v-if="isSkip" class="mt-2">
                      <a @click="skipHandler" class="link-text">
                        {{ $t("Skip") }}
                      </a>
                    </div>
                    <div v-if="isBack" class="mb-2">
                      <a @click="backHandler" class="link-text">
                        {{ $t("Back to Login") }}
                      </a>
                    </div>
                    <div class="pb-2">
                      <select
                        class="black--text"
                        v-model="selectedLanguage"
                        @change="languageChangeHandler"
                      >
                        <option
                          v-for="(item, index) in Languages"
                          :key="index"
                          :value="item.value"
                        >
                          {{ item.text }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex md2 class="display-none">
            <div class="information">
              <p class="info-title">
                {{ $t("Password must be between 8 and 16 characters long.") }}.
              </p>
              <hr :style="{ width: '80%', margin: 'auto', marginTop: '-9px' }" />
              <p class="info-sub-title">{{ $t("Its better to have") }}:</p>
              <ul class="info-list">
                <li>{{ $t("Upper & lower case letters") }}</li>
                <li>{{ $t("A symbol (#&$)") }}</li>
                <li>{{ $t("A numerical value (0-9)") }}</li>
                <li>{{ $t("Minimum characters = 8") }}</li>
                <li>{{ $t("Maximum characters = 16") }}</li>
              </ul>
            </div>
          </v-flex>
        </v-layout>
      </div>
    </v-content>
  </v-app>
</template>

<script>
import moment from "moment";
import { mapMutations, mapGetters, mapActions } from "vuex";
import { getClientSideInfo, setTheme } from "@/util/mixin/index.js";
import i18n from "@/plugins/i18n";
import Utils from "@/util/index.js";

export default {
  mixins: [getClientSideInfo, setTheme],
  data: () => ({
    showPassword: false,
    password: "",
    confirmPassword: "",
    buttonStatus: false,
    isSkip: false,
    user: "",
    email: "",
    rules: {
      required: (v) => !!v || "Password is required",
      min: (v) => v.length >= 8 || "Use 8 characters or more for your password",
      max: (v) => v.length <= 16 || "Use 16 characters or less for your password",
    },
    Languages: [
      {
        text: "English",
        value: "en",
      },
      {
        text: "French",
        value: "fr",
      },
    ],
    selectedLanguage: "en",
  }),
  mounted() {
    let infoBuffer = "";
    let infoJson = {};
    this.selectedLanguage = this.getGeneralState("language");
    i18n.locale = this.selectedLanguage;
    if (this.$route.query.info) {
      infoBuffer = Buffer.from(this.$route.query.info, "base64").toString();
      infoJson = JSON.parse(infoBuffer);
      this.SET_PASSWORD_CHANGE_STATUS(infoJson.status);
    }

    if (this.getGeneralState("passwordChangeStatus") == "update") {
      this.isSkip = this.getGeneralState("isPasswordUpdateSkip");
      this.user = this.getGeneralState("userData")["userName"];
      this.email = this.getGeneralState("userData")["email"];
    } else if (this.getGeneralState("passwordChangeStatus") == "new") {
      // When landing to this page via update link
      this.user = infoJson.user;
      this.email = infoJson.email;
      if (infoJson.valid < moment().unix()) {
        this.SET_SHOW_SNACKBAR(true);
        this.SET_SNACKBAR_CONTENT({
          icon: "error",
          header: "Error",
          message: "This link has expired",
        });
        this.$router.push("/404");
      }
    } else if (this.getGeneralState("passwordChangeStatus") == "reset") {
      this.email = this.getGeneralState("userData")["email"];
    }
  },
  computed: {
    ...mapGetters(["getRules", "getGeneralState", "getPlantList"]),
    computedPasswordStrength() {
      return Utils.checkPasswordStrength(this.password);
    },
    progressValue() {
      return this.computedPasswordStrength.score * 25;
    },
    isBack() {
      return this.getGeneralState("passwordChangeStatus") == "update";
    },
    computedPageName() {
      if (
        this.getGeneralState("passwordChangeStatus") == "update" ||
        this.getGeneralState("passwordChangeStatus") == "reset"
      ) {
        return (
          this.getGeneralState("passwordChangeStatus").charAt(0).toUpperCase() +
          this.getGeneralState("passwordChangeStatus").slice(1)
        );
      }
      if (this.getGeneralState("passwordChangeStatus") == "new") {
        return "Set";
      }
      return "";
    },
  },
  methods: {
    ...mapActions(["updatePasswordAction", "logoutAction", "fetchActivePlantDetails"]),
    ...mapMutations([
      "SET_THEME",
      "SET_SHOW_SNACKBAR",
      "SET_SNACKBAR_CONTENT",
      "SET_PASSWORD_UPDATE_SKIP",
      "SET_LANGUAGE",
    ]),
    async validatePasswordHandler() {
      if (this.password.length < 8 || this.password.length > 16) {
        this.SET_SHOW_SNACKBAR(true);
        this.SET_SNACKBAR_CONTENT({
          icon: "error",
          header: "Error",
          message: "Password must be between 8 and 16 characters.",
        });
        return;
      }
      if (this.password !== this.confirmPassword) {
        this.SET_SHOW_SNACKBAR(true);
        this.SET_SNACKBAR_CONTENT({
          icon: "error",
          header: "Error",
          message: "Password does not match",
        });
        return;
      }
      if (this.computedPasswordStrength.score == 4) {
        this.buttonStatus = true;
        // Get the user from where ever possible
        try {
          await this.updatePasswordAction({
            status: this.getGeneralState("passwordChangeStatus"),
            user: this.user ? this.user : null,
            email: this.email,
            password: this.password,
            info: await this.clientsInfo(),
          });

          if (this.getGeneralState("passwordChangeStatus") == "update") {
            let address = "";
            if (this.getGeneralState("showHomeDB")) {
              address = this.getRules(["rule", "access_config", "home", "path"]);
            } else {
              address = this.getRules(["rule", "access_config", "plantOverview", "path"]);
            }
            this.$router.push({ path: address });
          } else {
            this.$router.push({
              name: "login",
            });
          }
        } catch (err) {
          this.buttonStatus = false;
        }
      } else {
        this.SET_SHOW_SNACKBAR(true);
        this.SET_SNACKBAR_CONTENT({
          icon: "error",
          header: "Error",
          message: "Your password is not strong. Please fulfill the criteria!",
        });
      }
    },
    async skipHandler() {
      await this.fetchActivePlantDetails(this.getPlantList[0].id);

      if (this.getGeneralState("theme") == "light") {
        this.SET_THEME(false);
      } else {
        this.SET_THEME(true);
      }
      this.SET_PASSWORD_UPDATE_SKIP(true);
      this.SET_SHOW_SNACKBAR(true);
      this.SET_SNACKBAR_CONTENT({
        icon: "warning",
        header: "warning",
        message: this.$t("You have 10 days to reset the password", {
          num: 90 - this.getGeneralState("passwordResetDiff"),
        }),
      });
      if (this.getGeneralState("showHomeDB")) {
        this.$router.push(`${this.getRules(["rule", "access_config", "home", "path"])}`);
      } else {
        this.$router.push(
          `${this.getRules(["rule", "access_config", "plantOverview", "path"])}`
        );
      }
    },
    async backHandler() {
      try {
        await this.logoutAction();
        this.setDark();
        this.$router.push({
          name: "login",
        });
      } catch (err) {}
    },
    languageChangeHandler(event) {
      i18n.locale = event.target.value;
      this.SET_LANGUAGE(event.target.value);
    },
  },
};
</script>

<style scoped>
#overlay {
  position: absolute;
  height: 100%;
  width: 100%;
  z-index: 1;
  opacity: 0.8;
  background: #000;
}

.location-disabled-msg {
  position: absolute;
  width: 100%;
  height: 5vh;
  z-index: 2;
  color: white;
  font-size: 17px;
  text-align: center;
  line-height: 5vh;
  background: var(--v-accent-base);
}

.login-heading {
  margin: 0 0 25px;
  font-family: "Montserrat", sans-serif;
  font-size: 22px;
  font-weight: 400;
  color: #0e3f6d;
}

.enerman {
  background-image: url("./../assets/login/solar.jpg");
  background-color: #cccccc;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  position: relative;
  /* opacity: 0.65; */
  height: 100vh;
  text-align: center;
  display: -webkit-box;
  display: -moz-box;
  display: -ms-flexbox;
  display: -webkit-flex;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 30px 0;
}

.login-box {
  display: flex;
  flex-flow: row wrap;
  /* margin: 0 150px 0 200px; */
  max-width: 700px;
  border-radius: 10px;
  box-shadow: 0 0 35px rgba(0, 0, 0, 0.1);
  background: #fff;
}

.details {
  padding: 30px 60px 30px 0;
}

.details p a {
  margin-left: 3px;
  color: #717171;
}

.details p {
  margin-bottom: 0;
  font-size: 14px;
}

.bg-img {
  position: relative;
  background-size: cover;
  background-color: var(--v-primary-base);
  border-radius: 10px;
  padding: 80px 30px;
  margin: 30px 0;
  left: -60px;
}

.login-inner-form .btn-md {
  cursor: pointer;
  padding: 10px 50px 8px 50px;
  letter-spacing: 1px;
  font-size: 15px;
  font-weight: 400;
  height: 45px;
  border-radius: 3px;
  text-transform: uppercase;
}

.login-inner-form button:focus {
  outline: none;
  outline: 0 auto -webkit-focus-ring-color;
}

.login-inner-form .btn-theme.focus,
.btn-theme:focus {
  box-shadow: none;
}

.login-inner-form .btn-theme {
  background-color: var(--v-primary-base) !important;
  border: none;
  color: #fff;
}

.login-inner-form .btn-theme:hover {
  background: #194c7d;
  box-shadow: 0 0 35px rgba(0, 0, 0, 0.1);
}

.information {
  margin-top: 5vh;
  width: 18vw;
  /* height: 14rem; */
  background: #fff;
  padding: 18px;
  border-radius: 1rem;
  box-shadow: 0 0.125rem 0.5rem rgba(0, 0, 0, 0.3),
    0 0.0625rem 0.125rem rgba(0, 0, 0, 0.2);
}

.information::before {
  content: "";
  position: absolute;
  width: 0;
  height: 0;
  top: 42vh;
  right: 37.2%;
  border: 0.75rem solid transparent;
  border-top: none;
  border-bottom-color: #fff;
  -webkit-filter: drop-shadow(0 -0.0625rem 0.0625rem rgba(0, 0, 0, 0.1));
  filter: drop-shadow(0 -0.0625rem 0.0625rem rgba(0, 0, 0, 0.1));
  transform: rotate(-90deg);
}

.info-title {
  font-weight: 500;
  font-size: 16px;
  line-height: 15px;
  color: #6b6b6b;
}

.info-sub-title {
  text-align: left;
  width: 80%;
  color: #6b6b6b;
  margin: 10px auto;
  line-height: 15px;
}

.info-list {
  width: 80%;
  color: #6b6b6b;
  text-align: left;
  padding-left: 60px;
}

.btn-secondary {
  cursor: pointer;
  padding: 10px;
  font-size: 15px;
  font-weight: 400;
  height: 20px;
  border-radius: 3px;
  text-transform: uppercase;
}

/** MEDIA **/
@media (max-width: 1264px) {
  .login-box {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .bg-img {
    padding: 0;
    left: 0px;
    margin: -25px 25px;
    width: calc(100% - 50px);
  }

  .details {
    padding: 30px;
  }

  .display-none {
    display: none;
  }
}

select {
  display: inline-block;
  color: black;
  font-size: 15px;
  font-weight: 500;
  border-radius: 8px;
  padding: 0px 12px;
  width: 160px;
  height: 36px;
  margin: 0;
  border-color: #000;
  box-sizing: border-box;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border: 1.5px solid;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23333333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-position: right 8px center;
}

select:active {
  background-image: linear-gradient(45deg, black 50%, transparent 50%),
    linear-gradient(135deg, transparent 50%, black 50%);
  background-position: 134px 13px, 129px 13px;
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
  border-color: none;
  outline: 0;
}
</style>
